import { useEffect, useState } from "react";
import { Link, useLocation, useNavigationType } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { captureNotice } from "@/lib/sentry";
import mascotRabbit from "@/assets/error-404-rabbit.png";
import mascotSnake from "@/assets/error-404-snake.png";

// The two illustrations already carry the "404"/"ERROR" branding, so the mascot
// is the hero and the layout copy stays about the message + the way back.
const MASCOTS = [mascotRabbit, mascotSnake];

const NotFound = () => {
  const location = useLocation();
  const navigationType = useNavigationType();
  const { t } = useTranslation();
  // Pick a mascot once per mount. The 404 is client-only (react-router wildcard,
  // not SSR'd), so Math.random() here can't cause a hydration mismatch.
  const [mascot] = useState(() => MASCOTS[Math.floor(Math.random() * MASCOTS.length)]);

  useEffect(() => {
    console.error("404 Error: User attempted to access non-existent route:", location.pathname);
    // PUSH/REPLACE: one of our own links or redirects led here, a broken link
    // we can fix (warning). POP: a landing, reload or back/forward — usually a
    // typed, stale or external address (info). Not location.key: it survives a
    // reload in history.state, so a reloaded 404 would pass for an in-app one.
    const inApp = navigationType !== "POP";
    const nav = inApp ? "in_app" : "landing";
    // Stable message, pathname in extra: one issue per kind of 404, not one per
    // URL, so bots probing addresses can't spam it.
    captureNotice("In-app 404 (route not found)", {
      level: inApp ? "warning" : "info",
      tags: { nav },
      // document.referrer is set once per page load, so it only describes a landing.
      extra: { pathname: location.pathname, referrer: inApp ? null : document.referrer || null },
      fingerprint: ["in-app-404", nav],
    });
  }, [location.pathname, navigationType]);

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Helmet>
        <title>{t("notFound.title")} | Jynio</title>
        <meta name="robots" content="noindex" />
      </Helmet>
      <Header />
      <main className="flex-1 flex items-center justify-center px-4 pt-24 pb-16">
        <div className="flex flex-col items-center text-center max-w-xl">
          <img
            src={mascot}
            alt={t("notFound.mascotAlt")}
            decoding="async"
            className="w-64 sm:w-72 md:w-80 h-auto mb-8 select-none drop-shadow-sm animate-scale-in motion-reduce:animate-none"
          />
          <h1 className="font-display text-3xl md:text-4xl font-bold text-foreground mb-3 tracking-tight animate-fade-in motion-reduce:animate-none">
            {t("notFound.title")}
          </h1>
          <p className="text-base md:text-lg text-muted-foreground mb-8 max-w-md leading-relaxed animate-fade-in motion-reduce:animate-none">
            {t("notFound.description")}
          </p>
          <Button asChild size="lg">
            <Link to="/">{t("notFound.homeButton")}</Link>
          </Button>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default NotFound;
